import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useUser } from '@/contexts/UserContext';

export interface UnifiedPayment {
  id: string;
  type: 'schedule' | 'task';
  deal_id: string;
  deal_title: string;
  title: string;
  amount: number;
  due_date: string;
  status: 'pending' | 'paid' | 'overdue';
  payment_date?: string;
  paid_at?: string;
  payment_method?: string;
  reference_number?: string;
  notes?: string;
  task_id?: string;
  assigned_to?: string;
  task_status?: 'pending' | 'in_progress' | 'completed';
  original_record: any;
}

export interface PaymentSummary {
  totalAmount: number;
  paidAmount: number;
  pendingAmount: number;
  overdueAmount: number;
  upcomingIn7Days: number;
  upcomingIn30Days: number;
  paidCount: number;
  pendingCount: number;
  overdueCount: number;
}

export const useAllPayments = () => {
  const [payments, setPayments] = useState<UnifiedPayment[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { currentUser } = useUser();

  const fetchAllPayments = async () => {
    if (!currentUser?.id) return;
    
    setLoading(true);
    setError(null);

    try {
      // Step 1: Fetch all accessible deals (RLS will filter access)
      const { data: deals, error: dealsError } = await supabase
        .from('deals')
        .select('id, title');

      if (dealsError) throw dealsError;

      const dealIds = deals?.map(d => d.id) || [];

      // Step 2: Fetch payment schedules (conditionally filter by dealIds or let RLS handle it)
      let schedulesResult;
      if (dealIds.length > 0) {
        schedulesResult = await supabase
          .from('payment_schedules')
          .select('*')
          .in('deal_id', dealIds);
      } else {
        schedulesResult = await supabase
          .from('payment_schedules')
          .select('*');
      }

      if (schedulesResult.error) throw schedulesResult.error;

      // Step 3: Fetch all task payments (conditionally filter by dealIds or let RLS handle it)
      let tasksResult;
      if (dealIds.length > 0) {
        tasksResult = await supabase
          .from('deal_tasks')
          .select('*')
          .in('deal_id', dealIds);
      } else {
        tasksResult = await supabase
          .from('deal_tasks')
          .select('*');
      }

      if (tasksResult.error) throw tasksResult.error;

      // Create a deal map for quick lookup
      const dealMap = new Map(deals?.map(d => [d.id, d.title]) || []);

      const today = new Date().toISOString().split('T')[0];

      // Normalize payment schedules
      const normalizedSchedules: UnifiedPayment[] = (schedulesResult.data || []).map(schedule => {
        const currentStatus = schedule.status as 'pending' | 'paid' | 'overdue';
        const status: 'pending' | 'paid' | 'overdue' = 
          currentStatus === 'pending' && schedule.due_date < today ? 'overdue' : currentStatus;
        
        return {
          id: schedule.id,
          type: 'schedule' as const,
          deal_id: schedule.deal_id,
          deal_title: dealMap.get(schedule.deal_id) || 'Unknown Deal',
          title: schedule.milestone_name,
          amount: schedule.amount,
          due_date: schedule.due_date,
          status,
          payment_date: schedule.payment_date || undefined,
          payment_method: schedule.payment_method || undefined,
          reference_number: schedule.reference_number || undefined,
          notes: schedule.notes || undefined,
          original_record: schedule,
        };
      });

      // Normalize task payments - include ALL tasks with payment amounts
      const normalizedTasks: UnifiedPayment[] = (tasksResult.data || [])
        .filter(task => 
          task.payment_amount != null &&
          Number(task.payment_amount) > 0
        )
        .map(task => {
          // Determine payment status based on payment_status and due date
          let status: 'pending' | 'paid' | 'overdue';
          
          if (task.payment_status === 'paid') {
            status = 'paid';
          } else if (task.due_date && task.due_date < today) {
            status = 'overdue';
          } else {
            status = 'pending';
          }
          
          return {
            id: task.id,
            type: 'task' as const,
            deal_id: task.deal_id,
            deal_title: dealMap.get(task.deal_id) || 'Unknown Deal',
            title: task.title,
            amount: Number(task.payment_amount || 0),
            due_date: task.due_date || task.created_at,
            status,
            paid_at: task.paid_at || undefined,
            task_id: task.id,
            assigned_to: task.assigned_to || undefined,
            task_status: task.status as 'pending' | 'in_progress' | 'completed',
            original_record: task,
          };
        });

      // Combine and sort by due date
      const allPayments = [...normalizedSchedules, ...normalizedTasks]
        .sort((a, b) => new Date(a.due_date).getTime() - new Date(b.due_date).getTime());

      setPayments(allPayments);
    } catch (err) {
      console.error('Error fetching payments:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch payments');
    } finally {
      setLoading(false);
    }
  };

  const getPaymentSummary = (): PaymentSummary => {
    const today = new Date();
    const in7Days = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);
    const in30Days = new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000);

    const totalAmount = payments.reduce((sum, p) => sum + p.amount, 0);
    const paidAmount = payments.filter(p => p.status === 'paid').reduce((sum, p) => sum + p.amount, 0);
    const pendingAmount = payments.filter(p => p.status === 'pending').reduce((sum, p) => sum + p.amount, 0);
    const overdueAmount = payments.filter(p => p.status === 'overdue').reduce((sum, p) => sum + p.amount, 0);

    const upcomingIn7Days = payments
      .filter(p => {
        const dueDate = new Date(p.due_date);
        return p.status !== 'paid' && dueDate >= today && dueDate <= in7Days;
      })
      .reduce((sum, p) => sum + p.amount, 0);

    const upcomingIn30Days = payments
      .filter(p => {
        const dueDate = new Date(p.due_date);
        return p.status !== 'paid' && dueDate >= today && dueDate <= in30Days;
      })
      .reduce((sum, p) => sum + p.amount, 0);

    return {
      totalAmount,
      paidAmount,
      pendingAmount,
      overdueAmount,
      upcomingIn7Days,
      upcomingIn30Days,
      paidCount: payments.filter(p => p.status === 'paid').length,
      pendingCount: payments.filter(p => p.status === 'pending').length,
      overdueCount: payments.filter(p => p.status === 'overdue').length,
    };
  };

  useEffect(() => {
    fetchAllPayments();
  }, [currentUser?.id]);

  return {
    payments,
    loading,
    error,
    refetch: fetchAllPayments,
    summary: getPaymentSummary(),
  };
};
